/**
 * Itinerary Submissions Component
 * Form for submitting coordination itinerary data based on Survey123 structure
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Divider,
  Card,
  CardContent,
  CardHeader,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
  Map as MapIcon,
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { format, parse, addMinutes } from 'date-fns';
import {
  toDateInputValue,
  mergeDateWithExistingTimeLocal,
} from '../utils/routeRunDate';
import * as itineraryApi from '../services/itineraryApi';
import { useAuth } from '@signals/app-shell/hooks/useAuth';
import { useParticipantLabels } from '@signals/app-shell/hooks/useParticipantLabels';
import { useItinerarySubmissionStore } from '../stores/itinerarySubmissionStore';
import { useStudentProfileStore } from '../stores/studentProfileStore';

interface ItinerarySubmissionsProps {
  onEnterMapMode?: (entryIndex: number) => void;
  selectedLocation?: { lat: number; lon: number; address?: string; entryIndex: number } | null;
  onRouteEntriesChange?: (entries: itineraryApi.RouteDataEntry[]) => void;
  /** Called after a map pick is applied so parent can clear shared selectedLocation (avoids stale re-apply). */
  onSelectedLocationConsumed?: () => void;
}

const ItinerarySubmissions: React.FC<ItinerarySubmissionsProps> = ({
  onEnterMapMode,
  selectedLocation,
  onRouteEntriesChange,
  onSelectedLocationConsumed,
}) => {
  const { singular, singularLower } = useParticipantLabels();
  // Auth context
  const { user } = useAuth();
  const isStudent = user?.roles?.includes('student');
  const usernameLower = user?.username?.toLowerCase() || '';
  const isTrainee = usernameLower.includes('trainee');
  const isStudentInUsername = usernameLower.includes('student');
  const isStudentOrTrainee = isStudent || isTrainee || isStudentInUsername;
  
  // Helper function to extract student number from username
  // Handles both "Student-X" and "Trainee-X" formats
  const extractStudentNumber = (username: string): string | null => {
    if (!username) return null;
    
    // Try to match Student-X or Trainee-X patterns
    const studentMatch = username.match(/Student[_-]?(\d+)/i);
    const traineeMatch = username.match(/Trainee[_-]?(\d+)/i);
    
    if (traineeMatch) {
      return `Trainee-${traineeMatch[1].padStart(2, '0')}`;
    }
    if (studentMatch) {
      return `Student-${studentMatch[1].padStart(2, '0')}`;
    }
    
    return null;
  };
  
  // Auto-select student number from username for students/trainees
  const prefixedStudentNumber = isStudentOrTrainee && user?.username ? 
    extractStudentNumber(user.username) || '' 
    : '';
  
  // In-progress form data lives in Zustand so tab switches/remounts do not lose work.
  const savedSubmissionId = useItinerarySubmissionStore((state) => state.savedSubmissionId);
  const studentNumber = useItinerarySubmissionStore((state) => state.studentNumber);
  const routeRunDateTime = useItinerarySubmissionStore((state) => state.routeRunDateTime);
  const gender = useItinerarySubmissionStore((state) => state.gender);
  const height = useItinerarySubmissionStore((state) => state.height);
  const weight = useItinerarySubmissionStore((state) => state.weight);
  const hairTypeColor = useItinerarySubmissionStore((state) => state.hairTypeColor);
  const facialHairTypeColor = useItinerarySubmissionStore((state) => state.facialHairTypeColor);
  const vehMake = useItinerarySubmissionStore((state) => state.vehMake);
  const vehModel = useItinerarySubmissionStore((state) => state.vehModel);
  const vehColor = useItinerarySubmissionStore((state) => state.vehColor);
  const vehPlate = useItinerarySubmissionStore((state) => state.vehPlate);
  const routeEntries = useItinerarySubmissionStore((state) => state.routeEntries);
  const notes = useItinerarySubmissionStore((state) => state.notes);
  const uploadedFiles = useItinerarySubmissionStore((state) => state.uploadedFiles);
  const currentEntryIndex = useItinerarySubmissionStore((state) => state.currentEntryIndex);
  const setField = useItinerarySubmissionStore((state) => state.setField);
  const setRouteEntries = useItinerarySubmissionStore((state) => state.setRouteEntries);
  const setUploadedFiles = useItinerarySubmissionStore((state) => state.setUploadedFiles);
  const setCurrentEntryIndex = useItinerarySubmissionStore((state) => state.setCurrentEntryIndex);
  const resetForm = useItinerarySubmissionStore((state) => state.resetForm);

  const profileVersion = useStudentProfileStore((state) => state.profileVersion);
  const syncedProfileStudentNumber = useStudentProfileStore((state) => state.studentNumber);
  const lastAppliedProfileVersion = React.useRef(0);

  const setStudentNumber = (value: string) => setField('studentNumber', value);
  const setRouteRunDateTime = (value: string) => setField('routeRunDateTime', value);
  const setGender = (value: string) => setField('gender', value);
  const setHeight = (value: string) => setField('height', value);
  const setWeight = (value: string) => setField('weight', value);
  const setHairTypeColor = (value: string) => setField('hairTypeColor', value);
  const setFacialHairTypeColor = (value: string) => setField('facialHairTypeColor', value);
  const setVehMake = (value: string) => setField('vehMake', value);
  const setVehModel = (value: string) => setField('vehModel', value);
  const setVehColor = (value: string) => setField('vehColor', value);
  const setVehPlate = (value: string) => setField('vehPlate', value);
  const setNotes = (value: string) => setField('notes', value);
  
  // Choice lists
  const [choiceLists, setChoiceLists] = useState<itineraryApi.ChoiceLists>({
    student_numbers: [],
    route_types: [],
    route_components: [],
    genders: [],
  });
  
  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingProfile, setLoadingProfile] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [savingAs, setSavingAs] = useState<itineraryApi.ItinerarySubmissionStatus | null>(null);
  const [mapModeEntryIndex, setMapModeEntryIndex] = useState<number | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [deleteTargetIndex, setDeleteTargetIndex] = useState<number | null>(null);
  
  // Load choice lists on component mount
  useEffect(() => {
    const loadChoiceLists = async () => {
      try {
        const lists = await itineraryApi.getChoiceLists();
        setChoiceLists(lists);
        
        // Auto-select student number for students/trainees after choice lists load
        if (isStudentOrTrainee && prefixedStudentNumber && !studentNumber) {
          setStudentNumber(prefixedStudentNumber);
          loadStudentProfile(prefixedStudentNumber);
        }
      } catch (err: any) {
        console.error('Error loading choice lists:', err);
        setError('Failed to load form options');
      }
    };
    
    loadChoiceLists();
  }, []);
  
  // Notify parent when route entries change
  useEffect(() => {
    if (onRouteEntriesChange) {
      onRouteEntriesChange(routeEntries);
    }
  }, [routeEntries, onRouteEntriesChange]);
  
  // Load student profile when student is selected
  const loadStudentProfile = useCallback(async (studentNum: string) => {
    if (!studentNum) return;
    
    setLoadingProfile(true);
    setError(null);
    const profileVersionBeforeRequest = useStudentProfileStore.getState().profileVersion;
    
    try {
      const profile = await itineraryApi.getStudentProfile(studentNum);

      if (useStudentProfileStore.getState().profileVersion > profileVersionBeforeRequest) {
        return;
      }
      
      // Autopopulate form fields from profile
      setGender(profile.gender || '');
      setHeight(profile.height || '');
      setWeight(profile.weight || '');
      setHairTypeColor(profile.hair_typecolor || '');
      setFacialHairTypeColor(profile.facial_hair_typecolor || '');
      setVehMake(profile.veh_make || '');
      setVehModel(profile.veh_model || '');
      setVehColor(profile.veh_color || '');
      setVehPlate(profile.veh_plate || '');
      
      setSuccess('Profile data loaded successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      if (err.response?.status === 404) {
        const syncedProfile = useStudentProfileStore.getState();
        if (syncedProfile.profileVersion > 0 && syncedProfile.studentNumber === studentNum) {
          return;
        }

        // Profile not found - clear fields for manual entry
        setGender('');
        setHeight('');
        setWeight('');
        setHairTypeColor('');
        setFacialHairTypeColor('');
        setVehMake('');
        setVehModel('');
        setVehColor('');
        setVehPlate('');
        
        setError(`No profile found for this ${singularLower}. Please enter data manually.`);
      } else {
        setError(`Failed to load ${singularLower} profile`);
      }
    } finally {
      setLoadingProfile(false);
    }
  }, [singularLower, setGender, setHeight, setWeight, setHairTypeColor, setFacialHairTypeColor, setVehMake, setVehModel, setVehColor, setVehPlate]);

  // Apply admin-tab profile saves to the submission form without a page reload.
  useEffect(() => {
    if (profileVersion === 0 || profileVersion === lastAppliedProfileVersion.current) {
      return;
    }
    if (!syncedProfileStudentNumber) {
      return;
    }
    if (studentNumber && studentNumber !== syncedProfileStudentNumber) {
      return;
    }

    const profile = useStudentProfileStore.getState();
    lastAppliedProfileVersion.current = profileVersion;
    if (!studentNumber) {
      setStudentNumber(profile.studentNumber);
    }
    setGender(profile.gender);
    setHeight(profile.height);
    setWeight(profile.weight);
    setHairTypeColor(profile.hairTypeColor);
    setFacialHairTypeColor(profile.facialHairTypeColor);
    setVehMake(profile.vehMake);
    setVehModel(profile.vehModel);
    setVehColor(profile.vehColor);
    setVehPlate(profile.vehPlate);
    setError(null);
    setSuccess('Profile data updated from admin tab');
    setTimeout(() => setSuccess(null), 3000);
  }, [
    profileVersion,
    syncedProfileStudentNumber,
    studentNumber,
    setStudentNumber,
    setGender,
    setHeight,
    setWeight,
    setHairTypeColor,
    setFacialHairTypeColor,
    setVehMake,
    setVehModel,
    setVehColor,
    setVehPlate,
  ]);
  
  // Handle student selection
  const handleStudentSelect = (studentNum: string) => {
    setStudentNumber(studentNum);
    loadStudentProfile(studentNum);
  };
  
  // Add new route entry
  const handleAddRouteEntry = () => {
    const fallbackDateTime = routeRunDateTime || format(new Date(), "yyyy-MM-dd'T'HH:mm");
    const lastEntry = routeEntries.length > 0 ? routeEntries[routeEntries.length - 1] : null;
    const startDateTime = lastEntry?.depart_datetime || fallbackDateTime;
    const endDateTime = format(
      addMinutes(parse(startDateTime, "yyyy-MM-dd'T'HH:mm", new Date()), 5),
      "yyyy-MM-dd'T'HH:mm"
    );

    const newEntry: itineraryApi.RouteDataEntry = {
      entry_order: routeEntries.length,
      route_type: 'Pre-route',
      route_component: 'SP',
      route_component_other: '',
      latitude: 0,  // Always start fresh, not defaulting to last point
      longitude: 0,
      location_name_address: '',
      arrive_datetime: startDateTime,
      depart_datetime: endDateTime,
      has_documentation: false,
      notes: '',
    };
    
    setRouteEntries([...routeEntries, newEntry]);
    setCurrentEntryIndex(routeEntries.length); // Navigate to the new entry
  };
  
  const handleRouteRunDateChange = (value: string) => {
    const mergedDateTime = mergeDateWithExistingTimeLocal(value, routeRunDateTime);
    setRouteRunDateTime(mergedDateTime);
    if (value && routeEntries.length > 0) {
      setRouteEntries(routeEntries.map((entry) => ({
        ...entry,
        arrive_datetime: mergeDateWithExistingTimeLocal(value, entry.arrive_datetime),
        depart_datetime: mergeDateWithExistingTimeLocal(value, entry.depart_datetime),
      })));
    }
  };
  
  // Open delete confirmation dialog
  const handleDeleteClick = (index: number) => {
    setDeleteTargetIndex(index);
    setDeleteConfirmOpen(true);
  };
  
  // Confirm and remove route entry
  const handleConfirmDelete = () => {
    if (deleteTargetIndex !== null) {
      const updatedEntries = routeEntries.filter((_, i) => i !== deleteTargetIndex);
      // Update entry_order for remaining entries
      updatedEntries.forEach((entry, i) => {
        entry.entry_order = i;
      });
      setRouteEntries(updatedEntries);
      
      // Adjust current index if needed
      if (currentEntryIndex >= updatedEntries.length) {
        setCurrentEntryIndex(Math.max(0, updatedEntries.length - 1));
      } else if (currentEntryIndex > deleteTargetIndex) {
        setCurrentEntryIndex(currentEntryIndex - 1);
      }
    }
    setDeleteConfirmOpen(false);
    setDeleteTargetIndex(null);
  };
  
  // Cancel delete
  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setDeleteTargetIndex(null);
  };
  
  // Update route entry field
  const handleUpdateRouteEntry = (index: number, field: string, value: any) => {
    // Safety check: ensure the entry exists
    if (index < 0 || index >= routeEntries.length) {
      console.warn(`Cannot update route entry ${index}: index out of bounds (array length: ${routeEntries.length})`);
      return;
    }
    
    const updatedEntries = [...routeEntries];
    (updatedEntries[index] as any)[field] = value;
    
    // Auto-populate depart datetime when arrive datetime is set
    if (field === 'arrive_datetime' && value) {
      // If depart is empty or before arrive, set it to the same as arrive
      const currentDepart = updatedEntries[index].depart_datetime;
      if (!currentDepart || currentDepart < value) {
        (updatedEntries[index] as any)['depart_datetime'] = value;
      }
    }
    
    setRouteEntries(updatedEntries);
  };
  
  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray: File[] = Array.from(files);
      // Filter for allowed file types and size (max 10MB per file)
      const validFiles = fileArray.filter((file: File) => {
        const isValidType = /\.(pdf|doc|docx|jpg|jpeg|png)$/i.test(file.name);
        const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB
        
        if (!isValidType) {
          setError(`File ${file.name} has invalid type. Allowed: PDF, DOC, DOCX, JPG, PNG`);
        }
        if (!isValidSize) {
          setError(`File ${file.name} is too large. Maximum size: 10MB`);
        }
        
        return isValidType && isValidSize;
      });
      
      setUploadedFiles(prev => [...prev, ...validFiles]);
    }
    // Reset input
    event.target.value = '';
  };
  
  // Handle file removal
  const handleRemoveFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  // Enter map mode for location selection
  const handleEnterMapMode = (entryIndex: number) => {
    setMapModeEntryIndex(entryIndex);
    if (onEnterMapMode) {
      onEnterMapMode(entryIndex);
    } else {
      console.warn('ItinerarySubmissions: onEnterMapMode prop is not defined!');
    }
  };
  
  // Handle location selected from map
  useEffect(() => {
    if (selectedLocation && selectedLocation.entryIndex !== -1) {
      // Verify the entry exists before updating
      if (selectedLocation.entryIndex >= 0 && selectedLocation.entryIndex < routeEntries.length) {
        handleUpdateRouteEntry(selectedLocation.entryIndex, 'latitude', selectedLocation.lat);
        handleUpdateRouteEntry(selectedLocation.entryIndex, 'longitude', selectedLocation.lon);
        
        // If an address was provided from reverse geocoding, populate it
        if (selectedLocation.address) {
          handleUpdateRouteEntry(selectedLocation.entryIndex, 'location_name_address', selectedLocation.address);
        }
        
        setMapModeEntryIndex(null);
        onSelectedLocationConsumed?.();
      } else {
        console.warn(`Selected location for invalid entry index: ${selectedLocation.entryIndex}`);
      }
    }
  }, [selectedLocation, routeEntries.length, onSelectedLocationConsumed]);
  
  // Submit form
  const handleSubmit = async (status: itineraryApi.ItinerarySubmissionStatus) => {
    setLoading(true);
    setSavingAs(status);
    setError(null);
    setSuccess(null);
    
    // Validation
    if (!studentNumber) {
      setError(`Please select a ${singularLower} number`);
      setLoading(false);
      setSavingAs(null);
      return;
    }
    
    if (status === 'complete' && routeEntries.length === 0) {
      setError('Please add at least one route entry');
      setLoading(false);
      setSavingAs(null);
      return;
    }
    
    // Complete submissions must be review-ready; drafts can remain incomplete.
    if (status === 'complete') {
      for (let i = 0; i < routeEntries.length; i++) {
        const entry = routeEntries[i];
        if (!entry.location_name_address) {
          // Navigate to the entry with the error
          setCurrentEntryIndex(i);
          
          // Set error with clear guidance
          setError(`Entry ${i + 1} is missing a location. Please click "Select Location on Map" or enter an address.`);
          setLoading(false);
          setSavingAs(null);
          
          // Scroll to the route entries section
          setTimeout(() => {
            const routeEntriesCard = document.getElementById('route-entries-section');
            if (routeEntriesCard) {
              routeEntriesCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
          
          return;
        }
      }
    }
    
    try {
      const submission: itineraryApi.ItinerarySubmission = {
        student_number: studentNumber,
        route_run_datetime: new Date(routeRunDateTime).toISOString(),
        gender,
        height,
        weight,
        hair_typecolor: hairTypeColor,
        facial_hair_typecolor: facialHairTypeColor,
        veh_make: vehMake,
        veh_model: vehModel,
        veh_color: vehColor,
        veh_plate: vehPlate,
        status,
        notes,
        route_entries: routeEntries.map((entry, index) => ({
          ...entry,
          entry_order: index,
          arrive_datetime: new Date(entry.arrive_datetime).toISOString(),
          depart_datetime: new Date(entry.depart_datetime).toISOString(),
        })),
      };
      
      const result = savedSubmissionId
        ? await itineraryApi.updateItinerarySubmission(savedSubmissionId, submission)
        : await itineraryApi.createItinerarySubmission(submission);
      setField('savedSubmissionId', result.id || savedSubmissionId);
      
      // Upload files after a submission is marked ready for review.
      if (status === 'complete' && uploadedFiles.length > 0 && result.id) {
        let uploadErrors: string[] = [];
        for (const file of uploadedFiles) {
          try {
            await itineraryApi.uploadAttachment(result.id, file);
            console.log(`Uploaded: ${file.name}`);
          } catch (uploadErr: any) {
            console.error(`Failed to upload ${file.name}:`, uploadErr);
            uploadErrors.push(file.name);
          }
        }
        if (uploadErrors.length > 0) {
          setError(`Submission created, but failed to upload: ${uploadErrors.join(', ')}`);
        }
      }
      
      if (status === 'draft') {
        setSuccess(`Draft saved successfully! Submission ID: ${result.id}`);
        return;
      }
      
      setSuccess(`Itinerary marked complete and ready for review! Submission ID: ${result.id}${uploadedFiles.length > 0 ? ` (${uploadedFiles.length} file(s) attached)` : ''}`);
      
      resetForm();
      
    } catch (err: any) {
      console.error('Error saving itinerary:', err);
      setError(err.response?.data?.detail || 'Failed to save itinerary');
    } finally {
      setLoading(false);
      setSavingAs(null);
    }
  };
  
  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MapIcon color="primary" />
            Coordination Itinerary Submission
            {savedSubmissionId && <Chip label={`Draft #${savedSubmissionId}`} color="warning" size="small" />}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Save as draft while you work, then mark complete when ready for review
          </Typography>
        </Box>
      </Box>
      
      {/* Snackbar notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
      
      {/* Student Selection */}
      <Card sx={{ mb: 2 }}>
        <CardHeader
          avatar={<PersonIcon color="primary" />}
          title={`${singular} Information`}
          titleTypographyProps={{ variant: 'subtitle1' }}
        />
        <CardContent>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{`${singular} Number *`}</InputLabel>
              <Select
                value={studentNumber}
                onChange={(e) => handleStudentSelect(e.target.value)}
                label={`${singular} Number *`}
                disabled={loadingProfile || isStudentOrTrainee}
              >
                {(isStudentOrTrainee 
                  ? choiceLists.student_numbers.filter(num => num === prefixedStudentNumber)
                  : choiceLists.student_numbers
                ).map((num) => (
                  <MenuItem key={num} value={num}>
                    {num}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              label="Route Run Date *"
              type="date"
              value={toDateInputValue(routeRunDateTime)}
              onChange={(e) => handleRouteRunDateChange(e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </CardContent>
      </Card>
      
      {/* Admin Data - Collapsible */}
      {studentNumber && (
        <Accordion sx={{ mb: 2 }} defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="secondary" />
              <Box>
                <Typography variant="subtitle1">Admin Data</Typography>
                <Typography variant="caption" color="text.secondary">
                  {loadingProfile ? 'Loading profile...' : `Autopopulated from ${singularLower} profile`}
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <TextField
                label="Gender"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                size="small"
                fullWidth
                disabled={loadingProfile}
              />
              
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Height"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  size="small"
                  fullWidth
                  disabled={loadingProfile}
                  placeholder="e.g., 6'2&quot;"
                />
                
                <TextField
                  label="Weight"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  size="small"
                  fullWidth
                  disabled={loadingProfile}
                  placeholder="e.g., 180 lbs"
                />
              </Stack>
              
              <TextField
                label="Hair (Type/Color)"
                value={hairTypeColor}
                onChange={(e) => setHairTypeColor(e.target.value)}
                size="small"
                fullWidth
                disabled={loadingProfile}
                placeholder="e.g., Short/Brown"
              />
              
              <TextField
                label="Facial Hair (Type/Color)"
                value={facialHairTypeColor}
                onChange={(e) => setFacialHairTypeColor(e.target.value)}
                size="small"
                fullWidth
                disabled={loadingProfile}
                placeholder="e.g., Beard/Black"
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
      
      {/* Vehicle Data - Collapsible */}
      {studentNumber && (
        <Accordion sx={{ mb: 2 }} defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CarIcon color="info" />
              <Box>
                <Typography variant="subtitle1">Vehicle Data</Typography>
                <Typography variant="caption" color="text.secondary">
                  {loadingProfile ? 'Loading profile...' : `Autopopulated from ${singularLower} profile`}
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Make"
                  value={vehMake}
                  onChange={(e) => setVehMake(e.target.value)}
                  size="small"
                  fullWidth
                  disabled={loadingProfile}
                />
                
                <TextField
                  label="Model"
                  value={vehModel}
                  onChange={(e) => setVehModel(e.target.value)}
                  size="small"
                  fullWidth
                  disabled={loadingProfile}
                />
              </Stack>
              
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Color"
                  value={vehColor}
                  onChange={(e) => setVehColor(e.target.value)}
                  size="small"
                  fullWidth
                  disabled={loadingProfile}
                />
                
                <TextField
                  label="License Plate"
                  value={vehPlate}
                  onChange={(e) => setVehPlate(e.target.value)}
                  size="small"
                  fullWidth
                  disabled={loadingProfile}
                />
              </Stack>
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
      
      {/* Route Data Entries */}
      <Card id="route-entries-section" sx={{ mb: 2 }}>
        <CardHeader
          avatar={<LocationIcon color="error" />}
          title={`Route Data (${routeEntries.length} entries)`}
          action={
            <Tooltip title="Add a new location to your itinerary">
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddRouteEntry}
                size="small"
                variant="contained"
                color="primary"
              >
                Add Route Entry
              </Button>
            </Tooltip>
          }
          titleTypographyProps={{ variant: 'subtitle1' }}
        />
        <CardContent>
          {routeEntries.length === 0 ? (
            <Alert severity="info">
              Click "Add Route Entry" to add locations to your itinerary
            </Alert>
          ) : (
            <Box>
              {/* Pagination Controls */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <IconButton
                  onClick={() => setCurrentEntryIndex(Math.max(0, currentEntryIndex - 1))}
                  disabled={currentEntryIndex === 0}
                  size="small"
                >
                  <PrevIcon />
                </IconButton>
                
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography variant="h6" color="primary">
                    Entry {currentEntryIndex + 1} of {routeEntries.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    All {routeEntries.length} {routeEntries.length === 1 ? 'point' : 'points'} shown on map
                  </Typography>
                </Box>
                
                <IconButton
                  onClick={() => setCurrentEntryIndex(Math.min(routeEntries.length - 1, currentEntryIndex + 1))}
                  disabled={currentEntryIndex === routeEntries.length - 1}
                  size="small"
                >
                  <NextIcon />
                </IconButton>
              </Box>

              {/* Current Entry Form */}
              {routeEntries[currentEntryIndex] && (
                <Stack spacing={2}>
                  {/* Entry Summary */}
                  <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                      <Chip label={`#${currentEntryIndex + 1}`} color="primary" size="small" />
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {routeEntries[currentEntryIndex].route_type} - {routeEntries[currentEntryIndex].route_component}
                      </Typography>
                    </Box>
                    <Tooltip title="Delete this entry">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(currentEntryIndex)}
                        sx={{
                          bgcolor: 'white',
                          '&:hover': {
                            backgroundColor: 'error.main',
                            color: 'white',
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <FormControl fullWidth size="small">
                    <InputLabel>Route Type</InputLabel>
                    <Select
                      value={routeEntries[currentEntryIndex].route_type}
                      onChange={(e) => handleUpdateRouteEntry(currentEntryIndex, 'route_type', e.target.value)}
                      label="Route Type"
                    >
                      {choiceLists.route_types.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth size="small">
                    <InputLabel>Route Component</InputLabel>
                    <Select
                      value={routeEntries[currentEntryIndex].route_component}
                      onChange={(e) => handleUpdateRouteEntry(currentEntryIndex, 'route_component', e.target.value)}
                      label="Route Component"
                    >
                      {choiceLists.route_components.map((comp) => (
                        <MenuItem key={comp} value={comp}>
                          {comp}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  {routeEntries[currentEntryIndex].route_component === 'Other' && (
                    <TextField
                      label="Other Component Description"
                      value={routeEntries[currentEntryIndex].route_component_other || ''}
                      onChange={(e) => handleUpdateRouteEntry(currentEntryIndex, 'route_component_other', e.target.value)}
                      size="small"
                      fullWidth
                    />
                  )}
                  
                  <Divider>
                    <Chip label="Location" size="small" color={!routeEntries[currentEntryIndex].location_name_address && error ? 'error' : 'default'} />
                  </Divider>
                  
                  <Box>
                    <Button
                      startIcon={<MapIcon />}
                      onClick={() => handleEnterMapMode(currentEntryIndex)}
                      size="small"
                      variant="outlined"
                      fullWidth
                      color={!routeEntries[currentEntryIndex].location_name_address && error ? 'error' : (mapModeEntryIndex === currentEntryIndex ? 'secondary' : 'primary')}
                      sx={{
                        animation: !routeEntries[currentEntryIndex].location_name_address && error ? 'pulse 2s infinite' : 'none',
                        '@keyframes pulse': {
                          '0%': {
                            boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.4)',
                          },
                          '70%': {
                            boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)',
                          },
                          '100%': {
                            boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)',
                          },
                        },
                      }}
                    >
                      {mapModeEntryIndex === currentEntryIndex ? 'Click on map to select location' : 'Select Location on Map'}
                    </Button>
                    
                    <Typography variant="caption" display="block" sx={{ mt: 1, textAlign: 'center' }}>
                      Lat: {routeEntries[currentEntryIndex].latitude.toFixed(6)}, Lon: {routeEntries[currentEntryIndex].longitude.toFixed(6)}
                    </Typography>
                  </Box>
                  
                  <TextField
                    label="Location Name/Address *"
                    value={routeEntries[currentEntryIndex].location_name_address}
                    onChange={(e) => handleUpdateRouteEntry(currentEntryIndex, 'location_name_address', e.target.value)}
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    error={!routeEntries[currentEntryIndex].location_name_address && error ? true : false}
                    helperText={!routeEntries[currentEntryIndex].location_name_address && error ? 'This field is required' : ''}
                  />
                  
                  <Divider>
                    <Chip label="Timing" size="small" icon={<ScheduleIcon />} />
                  </Divider>

                  <TextField
                    label="Route Run Date"
                    type="date"
                    value={toDateInputValue(routeRunDateTime)}
                    onChange={(e) => handleRouteRunDateChange(e.target.value)}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    helperText="Updates the route date for all components; times are unchanged"
                  />
                  
                  <TextField
                    label="Arrive"
                    type="datetime-local"
                    value={routeEntries[currentEntryIndex].arrive_datetime}
                    onChange={(e) => handleUpdateRouteEntry(currentEntryIndex, 'arrive_datetime', e.target.value)}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                  
                  <TextField
                    label="Depart"
                    type="datetime-local"
                    value={routeEntries[currentEntryIndex].depart_datetime}
                    onChange={(e) => handleUpdateRouteEntry(currentEntryIndex, 'depart_datetime', e.target.value)}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                  
                  <Divider>
                    <Chip label="Notes" size="small" />
                  </Divider>
                  
                  <TextField
                    label="Entry Notes"
                    value={routeEntries[currentEntryIndex].notes || ''}
                    onChange={(e) => handleUpdateRouteEntry(currentEntryIndex, 'notes', e.target.value)}
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Notes specific to this location..."
                  />
                </Stack>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
      
      {/* Document Upload */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AttachFileIcon fontSize="small" />
            Attachments (Optional)
          </Typography>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            startIcon={<CloudUploadIcon />}
            sx={{ mb: 2 }}
          >
            Upload Document
            <input
              type="file"
              hidden
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={handleFileUpload}
              multiple
            />
          </Button>
          {uploadedFiles.length > 0 && (
            <Stack spacing={1}>
              {uploadedFiles.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                    <DescriptionIcon fontSize="small" color="primary" />
                    <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({(file.size / 1024).toFixed(1)} KB)
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveFile(index)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
      
      {/* Save Buttons */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button
          variant="outlined"
          color="warning"
          size="large"
          fullWidth
          startIcon={loading && savingAs === 'draft' ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={() => handleSubmit('draft')}
          disabled={loading || !studentNumber}
        >
          {loading && savingAs === 'draft' ? 'Saving Draft...' : 'Save Draft'}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          startIcon={loading && savingAs === 'complete' ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={() => handleSubmit('complete')}
          disabled={loading || !studentNumber || routeEntries.length === 0}
        >
          {loading && savingAs === 'complete' ? 'Saving...' : 'Mark Complete'}
        </Button>
      </Stack>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Route Entry?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete Entry #{deleteTargetIndex !== null ? deleteTargetIndex + 1 : ''}?
            {deleteTargetIndex !== null && routeEntries[deleteTargetIndex]?.location_name_address && (
              <>
                <br />
                <br />
                <strong>{routeEntries[deleteTargetIndex].location_name_address}</strong>
              </>
            )}
            <br />
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ItinerarySubmissions;
